import type { Venue } from "@coterie/types";
import venuesJson from "./data/venues.json";
import { metres, type Fix } from "./geo.ts";

export const venues = venuesJson as Venue[];

const byId = new Map(venues.map((v) => [v.id, v]));

export const venueName = (id: string) => byId.get(id)?.name ?? id;
export const venueStreet = (id: string) => byId.get(id)?.street ?? "";

const fold = (s: string) =>
  s.toLowerCase().normalize("NFD").replace(/\p{M}/gu, "").replace(/[^a-z0-9]+/g, " ").trim();

const proximityPool = () => {
  const curated = venues.filter((v) => v.curated);
  // unpruned corpus has no curated flags; fall back to the full list so seeders can still pick nearby
  return curated.length ? curated : venues;
};

export type Nearby = Venue & { m: number };

export const nearby = (fix: Fix): { list: Nearby[]; preselect: Nearby | null; note: string } => {
  const ranked = proximityPool()
    .map((v) => ({ ...v, m: metres(fix, v) }))
    .sort((a, b) => a.m - b.m);

  if (fix.accuracy < 50) {
    const list = ranked.slice(0, 3);
    return { list, preselect: list[0] ?? null, note: "" };
  }
  if (fix.accuracy <= 200) {
    return { list: ranked.slice(0, 8), preselect: null, note: "gps is a bit vague — pick one, or search" };
  }
  return { list: [], preselect: null, note: "couldn't place you — search instead" };
};

export const search = (q: string): Venue[] => {
  const needle = fold(q);
  if (needle.length < 1) return [];
  const hits: Venue[] = [];
  for (const v of venues) {
    if (fold(v.name).includes(needle) || fold(v.street).includes(needle)) {
      hits.push(v);
      if (hits.length >= 20) break;
    }
  }
  return hits;
};
